import { useEffect, useState, type FormEvent } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  Eye,
  EyeOff,
  LockKeyhole,
  MessageCircle,
  PhoneCall,
  TriangleAlert,
  UserPlus,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";
import { ApiError, isOtpChallenge } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { rememberOtpChallenge } from "../lib/otpChallenge";
import { Link, Redirect, useRouter } from "../lib/router";
import { takeReturnPath } from "../lib/returnPath";
import { AuthLayout } from "../components/AuthLayout";
import { Button, ButtonLink, Input, Label } from "../components/ui";

export function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentBlock, setPaymentBlock] = useState<string | null>(null);
  const { signIn, isAuthenticated } = useAuth();
  const { navigate } = useRouter();

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setShowPassword(false);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  if (isAuthenticated) return <Redirect to="/" />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!username.trim() || !password) {
      toast.error("Informe seu usuário ou e-mail e sua senha.");
      return;
    }

    setLoading(true);
    setPaymentBlock(null);
    try {
      const result = await signIn({ username, password });
      if (isOtpChallenge(result)) {
        rememberOtpChallenge(result);
        navigate("/login/verificacao", { replace: true });
        return;
      }
      navigate(takeReturnPath(), { replace: true });
    } catch (error) {
      if (error instanceof ApiError && [401, 404].includes(error.status)) {
        toast.error("Usuário, e-mail ou senha inválidos.");
      } else if (error instanceof ApiError && error.status === 402) {
        // Pendência financeira: o aviso precisa ficar fixo na tela, com o
        // contato do financeiro, e não sumir como um toast.
        setPaymentBlock(error.message);
      } else if (error instanceof ApiError && error.status === 423) {
        toast.error(
          "Acesso temporariamente bloqueado após cinco tentativas. Tente novamente em 15 minutos.",
        );
      } else if (error instanceof ApiError && error.status === 429) {
        toast.error("Muitas tentativas. Aguarde um minuto e tente novamente.");
      } else if (error instanceof ApiError && error.status === 503) {
        toast.error(error.message);
      } else {
        toast.error("Não foi possível acessar o sistema. Verifique sua conexão.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      <p className="eyebrow">Bem-vindo de volta</p>
      <h2>Acesse sua central</h2>
      <p className="login-subtitle">
        Entre com as mesmas credenciais que você já utiliza no helpdesk.
      </p>

      {paymentBlock && (
        <div className="login-payment-block" role="alert">
          <span className="login-payment-block-icon">
            <TriangleAlert />
          </span>
          <div>
            <strong>Acesso suspenso por pendência financeira</strong>
            <p>{paymentBlock}</p>
            <div className="login-payment-block-actions">
              <a href="tel:+554132961010">
                <PhoneCall />
                41 3296-1010
              </a>
              <a
                href="https://api.whatsapp.com/send/?phone=554132961010&text=Ol%C3%A1%2C+meu+acesso+ao+helpdesk+est%C3%A1+bloqueado+por+pend%C3%AAncia+financeira&type=phone_number&app_absent=0"
                target="_blank"
                rel="noreferrer"
              >
                <MessageCircle />
                Falar com o financeiro
              </a>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="login-form">
            <div>
              <Label htmlFor="username">Usuário ou e-mail</Label>
              <div className="input-with-icon">
                <UserRound />
                <Input
                  id="username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  placeholder="Digite seu usuário ou e-mail"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Senha</Label>
                <Link to="/recuperar-senha" className="forgot-password-link">
                  Esqueceu sua senha?
                </Link>
              </div>
              <div className="input-with-icon">
                <LockKeyhole />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Digite sua senha"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={showPassword ? "Ocultar senha" : "Exibir senha"}
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              </div>
            </div>
            <Button type="submit" size="lg" loading={loading} className="mt-2 w-full">
              Entrar no helpdesk
              {!loading && <ArrowRight className="size-[18px]" />}
            </Button>
      </form>

      <div className="login-alternative">
        <span>É seu primeiro acesso?</span>
        <ButtonLink to="/primeiro-acesso" variant="ghost" size="sm">
          <UserPlus />
          Criar meu acesso
        </ButtonLink>
      </div>

      <section className="login-channels" aria-labelledby="login-channels-title">
        <header>
          <span className="login-channels-icon">
            <MessageCircle />
          </span>
          <div>
            <strong id="login-channels-title">Outros canais</strong>
            <p>Abra um chamado ou fale com nossa equipe.</p>
          </div>
        </header>

        <div className="login-channel-actions">
          <a
            className="login-channel-action login-channel-action--ticket"
            href="https://wa.me/5541992516477"
            target="_blank"
            rel="noreferrer"
          >
            <MessageCircle />
            <span>
              <small>WhatsApp</small>
              Abrir chamado
            </span>
            <ArrowUpRight />
          </a>
          <a
            className="login-channel-action"
            href="https://api.whatsapp.com/send/?phone=554132961010&text=Ol%C3%A1%2C+gostaria+de+falar+com+um+Consultor+da+Mira&type=phone_number&app_absent=0"
            target="_blank"
            rel="noreferrer"
          >
            <MessageCircle />
            <span>
              <small>Atendimento</small>
              Falar com consultor
            </span>
            <ArrowUpRight />
          </a>
        </div>

        <details>
          <summary>
            Como abrir pelo WhatsApp
            <ChevronDown />
          </summary>
          <div className="login-channel-details">
            <p>
              Envie uma mensagem, escolha <strong>Abrir chamado</strong>,
              informe assunto e prioridade, e descreva por texto, áudio ou foto.
            </p>
            <p>
              Se o telefone não estiver vinculado, o sistema solicitará o
              e-mail cadastrado e enviará um código de verificação.
            </p>
            <p className="login-channel-phone">
              <PhoneCall />
              Dificuldades? Ligue para{" "}
              <a href="tel:+554132961010">41 3296-1010</a>
            </p>
          </div>
        </details>
      </section>

    </AuthLayout>
  );
}
